import { AllEndUserData } from '@components/members/components/allEndUserList';
import { APICONFIG } from '@config/api.config';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { IQueryResponse } from '@services/api';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

type AllEndUserQuery = {
	data: AllEndUserData[];
	error: Error | null;
	isLoading: boolean;
	isPending: boolean;
};

export const useAllEndUserQuery = (): IQueryResponse<Array<AllEndUserQuery>> => {
	const [shouldFetch, setShouldFetch] = useState(true);
	const [isDbChecked, setIsDbChecked] = useState(false);
	const userRoleType = useUserRoleType();

	const fetchAllUserData = async (): Promise<Array<AllEndUserQuery>> => {
		const apiEndpoint = userRoleType === 'RBDM' ? APICONFIG.ALL_END_USER_API_RBDM : APICONFIG.ALL_END_USER_API;
		const response = await APIService.getData<Array<AllEndUserQuery>>(`${apiEndpoint}`, {
			limit: '0',
		});
		if (response) {
			const db = await getDatabase();
			const collectionName = userRoleType === 'RBDM' ? 'all_rbdm' : 'all';
			const allEndUser = await db[collectionName].find().exec();
			const primaryId = allEndUser.map((doc: { primary: string }) => doc.primary);
			await db[collectionName].bulkRemove(primaryId);
			await db[collectionName].bulkInsert(response);
		}
		return response;
	};

	const checkDb = async (): Promise<boolean> => {
		const db = await getDatabase();
		const collectionName = userRoleType === 'RBDM' ? 'all_rbdm' : 'all';
		const result = await db[collectionName].find().exec();
		return result.length > 0;
	};

	useEffect(() => {
		if (!userRoleType) {
			return;
		}

		setIsDbChecked(false);
		setShouldFetch(true);

		const checkDatabase = async (): Promise<void> => {
			const exists = await checkDb();
			setShouldFetch(!exists);
			setIsDbChecked(true);
		};
		checkDatabase();
	}, [userRoleType]);

	const { data, error, isLoading, isPending, refetch, isFetching } = useQuery({
		queryKey: ['End User', userRoleType],
		queryFn: fetchAllUserData,
		enabled: isDbChecked && shouldFetch && !!userRoleType,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		refetch,
		isFetching,
	};
};
